import { ProCard } from "@ant-design/pro-components";
import { Flex } from "antd";
import { memo, useRef } from "react";
import RenderExtraContent from "../RenderExtraContent";
import ConversationContent from "../ConversationContent";
import MessageInput from "../MessageInput";
import ScrollLinkList from "../ScrollLinkList";
import { formatFiles } from "@/utils";
import { siteCNList } from "@/constants";
import styles from "./index.module.scss";
import ToasterComponents from "../ToasterComponents";

interface ChatWindowProps {
  agentData: any;
  dialogueMessages: any[];
  dialogueContentRef: any;
  curMode?: string | null;
  onClose: () => void;
  onRetry: (id: string) => void;
  onFileChange: (files: any) => void;
  onSendMessage: (values: any) => void;
  setDialogueMessages: (messages: any[]) => void;
  setIsCloseSession: (value: boolean) => void;
  closeSSE: () => void;
  scrollToBottom: () => void;
  hadleModalHidden: (flag: boolean) => void;
  getAgentInitList: () => Promise<void>; // 获取智能体初始化数据的函数
}

/**
 * 聊天窗口组件
 * @param agentData 初始化智能体的数据
 * @param dialogueMessages 聊天窗口对话数组
 * @param dialogueContentRef 对话内容框的ref
 * @param curMode 当前模式
 * @param onClose 关闭聊天窗口card
 * @param onRetry 重新发送会话方法(重试功能)
 * @param onFileChange 选择上传文件方法
 * @param onSendMessage 发送消息方法
 * @param setDialogueMessages 设置聊天内容
 * @param closeSSE 关闭sse方法
 * @param scrollToBottom 滚动到底部方法
 * @param curCsIdentifier 客服标识
 * @returns
 */
const ChatWindow = ({
  agentData,
  dialogueMessages,
  dialogueContentRef,
  curMode,
  onClose,
  onRetry,
  onFileChange,
  onSendMessage,
  setDialogueMessages,
  setIsCloseSession,
  closeSSE,
  scrollToBottom,
  hadleModalHidden,
  getAgentInitList,
}: ChatWindowProps) => {
  const cardRef = useRef<HTMLDivElement | null>(null);

  return (
    <>
      <ProCard
        ref={cardRef}
        bordered
        style={{
          width: agentData?.setting?.width
            ? `${agentData?.setting?.width}`
            : 400,
          height: agentData?.setting?.height
            ? `${agentData?.setting?.height}`
            : 500,
        }}
        headerBordered
        className={styles.aiServerCard}
        title={
          <Flex gap={8}>
            {agentData?.setting?.logo && (
              <img width={18} src={agentData?.setting?.logo} alt="Agent Logo" />
            )}
            <span>{agentData?.setting?.brand_name}</span>
          </Flex>
        }
        extra={
          <RenderExtraContent
            onClose={onClose}
            cardRef={cardRef}
            agentData={agentData}
            dialogueMessages={dialogueMessages}
            setIsCloseSession={setIsCloseSession}
            closeSSE={closeSSE}
            hadleModalHidden={hadleModalHidden}
            getAgentInitList={getAgentInitList}
          />
        }
      >
        <Flex
          vertical={true}
          justify="space-between"
          style={{ height: "100%" }}
        >
          <div
            className={`${styles.content} ${styles.chatContent}`}
            data-scroll-container
          >
            <ConversationContent
              messages={dialogueMessages}
              dialogueContentRef={dialogueContentRef}
              onRetry={onRetry}
              setDialogueMessages={setDialogueMessages}
              handleSendMessage={onSendMessage}
              privacy={agentData?.setting?.privacy}
            />
          </div>
          <div className={styles.bottomContent}>
            <ScrollLinkList settingObj={agentData?.setting} />
            <MessageInput
              curMode={curMode}
              handleSendMessage={onSendMessage}
              onFileChange={(files) => {
                const resultFile: any = formatFiles(files);
                onFileChange(resultFile);
                scrollToBottom();
              }}
            />
            {/* 中文站点才显示 */}
            {siteCNList.some((domain) =>
              window.location.hostname.includes(domain),
            ) && (
              <div className={styles.footerText}>内容由AI生成,请仔细甄别</div>
            )}
          </div>
        </Flex>
      </ProCard>
      <ToasterComponents />
    </>
  );
};

export default memo(ChatWindow);
